import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Shield, ChevronRight, Eye, Edit3, Trash2, Mail, Package,
  Lock, AlertTriangle, Users, Globe, Cpu, Link2, RefreshCw,
  Phone, MapPin, ChevronDown, Sparkles, ArrowRight
} from "lucide-react";

/* ─── Data ───────────────────────────────────────────────────── */
const sections = [
  { id: "about",      icon: Sparkles,      title: "About Our Company" },
  { id: "collect",    icon: Eye,           title: "Information We Collect" },
  { id: "use",        icon: Cpu,           title: "How We Use Your Information" },
  { id: "cookies",    icon: Package,       title: "Cookies & Tracking" },
  { id: "third",      icon: Link2,         title: "Third-Party Services" },
  { id: "security",   icon: Lock,          title: "Data Security" },
  { id: "sharing",    icon: Users,         title: "Data Sharing & Disclosure" },
  { id: "retention",  icon: RefreshCw,     title: "Data Retention Policy" },
  { id: "rights",     icon: Shield,        title: "Your Privacy Rights" },
  { id: "children",   icon: Users,         title: "Children's Privacy" },
  { id: "transfers",  icon: Globe,         title: "International Transfers" },
  { id: "ip",         icon: Edit3,         title: "Intellectual Property" },
  { id: "links",      icon: Link2,         title: "External Links" },
  { id: "changes",    icon: RefreshCw,     title: "Policy Changes" },
  { id: "contact",    icon: Mail,          title: "Contact Information" },
];

const trustBadges = [
  { icon: Lock,          label: "SSL Encrypted" },
  { icon: Trash2,        label: "No Data Selling" },
  { icon: Shield,        label: "GDPR Aligned" },
  { icon: Globe,         label: "India Based" },
];

/* ─── Sub-components ─────────────────────────────────────────── */
const BulletList = ({ items }) => (
  <ul className="mt-3 space-y-2">
    {items.map((item, i) => (
      <li key={i} className="flex items-start gap-3">
        <span className="mt-2 w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0" />
        <span className="text-gray-600 text-sm leading-relaxed">{item}</span>
      </li>
    ))}
  </ul>
);

const SubSection = ({ title, children }) => (
  <div className="mb-6">
    <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
      <span className="w-4 h-[2px] bg-emerald-400 rounded-full" />
      {title}
    </h4>
    {children}
  </div>
);

const InfoChip = ({ icon: Icon, text, color = "emerald" }) => {
  const styles = {
    emerald: "bg-emerald-50 border-emerald-100 text-emerald-700",
    red:     "bg-red-50 border-red-100 text-red-700",
    teal:    "bg-teal-50 border-teal-100 text-teal-700",
    green:   "bg-green-50 border-green-100 text-green-700",
  };
  return (
    <div className={`flex items-start gap-2.5 border rounded-xl px-4 py-3 text-sm ${styles[color]}`}>
      {Icon && <Icon size={15} className="flex-shrink-0 mt-0.5" />}
      <span>{text}</span>
    </div>
  );
};

/* ─── Section wrapper ────────────────────────────────────────── */
const Section = ({ id, icon: Icon, num, title, children }) => (
  <motion.div
    id={id}
    initial={{ opacity: 0, y: 16 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.35 }}
    className="scroll-mt-32 mb-12"
  >
    <div className="flex items-center gap-3 mb-5">
      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center flex-shrink-0 shadow-sm shadow-emerald-200">
        <Icon size={15} className="text-white" />
      </div>
      <h2 className="text-lg font-bold text-gray-800">
        <span className="text-emerald-500 font-semibold mr-1.5">{num}.</span>
        {title}
      </h2>
    </div>
    <div className="pl-11">{children}</div>
  </motion.div>
);

/* ─── Main Component ─────────────────────────────────────────── */
const PrivacyPolicy = () => {
  const [active, setActive] = useState("about");
  const [tocOpen, setTocOpen] = useState(false);

  useEffect(() => {
    const handler = () => {
      const found = [...sections]
        .reverse()
        .find(({ id }) => {
          const el = document.getElementById(id);
          return el && el.getBoundingClientRect().top <= 140;
        });
      if (found) setActive(found.id);
    };
    window.addEventListener("scroll", handler, { passive: true });
    return () => window.removeEventListener("scroll", handler);
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    setTocOpen(false);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* ── Hero ── */}
      <div className="relative bg-white border-b border-gray-200 overflow-hidden">
        {/* Dot grid background */}
        <div
          className="absolute inset-0 opacity-[0.035]"
          style={{
            backgroundImage: "radial-gradient(circle at 1px 1px, #10b981 1px, transparent 0)",
            backgroundSize: "28px 28px",
          }}
        />
        {/* Blobs */}
        <div className="absolute -top-20 -right-20 w-80 h-80 bg-emerald-100 rounded-full blur-3xl opacity-40 pointer-events-none" />
        <div className="absolute -bottom-10 -left-10 w-56 h-56 bg-teal-100 rounded-full blur-3xl opacity-30 pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-6 py-20">
          {/* Breadcrumb */}
          <div className="flex items-center gap-1.5 text-xs text-gray-400 mb-6">
            <Link to="/" className="hover:text-emerald-600 transition-colors">Home</Link>
            <ChevronRight size={12} />
            <span className="text-gray-600 font-medium">Privacy Policy</span>
          </div>

          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
            <div>
              {/* Live badge */}
              <div className="inline-flex items-center gap-2 bg-emerald-50 border border-emerald-100 text-emerald-700 text-xs font-semibold px-3 py-1.5 rounded-full mb-5">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                Effective 26 May 2026
              </div>
              <h1 className="text-4xl sm:text-5xl font-black text-gray-900 mb-3 tracking-tight leading-none">
                Privacy{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-500 to-emerald-600">
                  Policy
                </span>
              </h1>
              <p className="text-gray-500 text-base max-w-lg leading-relaxed">
                How <span className="text-emerald-600 font-semibold">OneNest Connect</span> collects,
                uses, and protects your information across all our services.
              </p>
            </div>

            {/* Trust badges */}
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-2 xl:grid-cols-4 gap-2.5">
              {trustBadges.map(({ icon: Icon, label }) => (
                <div
                  key={label}
                  className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5 text-xs text-gray-600 whitespace-nowrap"
                >
                  <Icon size={13} className="text-emerald-500 flex-shrink-0" />
                  {label}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── Body ── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
        <div className="flex gap-10 items-start">

          {/* Sidebar */}
          <aside className="hidden lg:block w-60 flex-shrink-0 sticky top-28 self-start">
            <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm">
              <div className="px-4 py-3 bg-gradient-to-r from-green-500 to-emerald-600 flex items-center gap-2">
                <Shield size={14} className="text-white" />
                <span className="text-white text-xs font-bold uppercase tracking-wider">Contents</span>
              </div>
              <nav className="py-2 max-h-[calc(100vh-180px)] overflow-y-auto">
                {sections.map(({ id, icon: Icon, title }, i) => (
                  <button
                    key={id}
                    onClick={() => scrollTo(id)}
                    className={`w-full text-left px-4 py-2.5 text-xs flex items-center gap-2.5 transition-all duration-150 group ${
                      active === id
                        ? "bg-emerald-50 text-emerald-700 font-semibold border-r-2 border-emerald-500"
                        : "text-gray-500 hover:bg-gray-50 hover:text-gray-700"
                    }`}
                  >
                    <Icon
                      size={13}
                      className={`flex-shrink-0 ${active === id ? "text-emerald-500" : "text-gray-300 group-hover:text-gray-400"}`}
                    />
                    <span className="leading-snug truncate">{title}</span>
                  </button>
                ))}
              </nav>
            </div>
          </aside>

          {/* Mobile TOC */}
          <div className="lg:hidden w-full mb-6">
            <button
              onClick={() => setTocOpen(!tocOpen)}
              className="w-full flex items-center justify-between bg-white border border-gray-200 rounded-xl px-5 py-3.5 text-sm font-semibold text-gray-700 shadow-sm"
            >
              <span className="flex items-center gap-2">
                <Shield size={15} className="text-emerald-500" />
                Table of Contents
              </span>
              <ChevronDown
                size={16}
                className={`text-gray-400 transition-transform ${tocOpen ? "rotate-180" : ""}`}
              />
            </button>
            <AnimatePresence>
              {tocOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  className="mt-2 bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden"
                >
                  {sections.map(({ id, icon: Icon, title }, i) => (
                    <button
                      key={id}
                      onClick={() => scrollTo(id)}
                      className="w-full text-left px-5 py-3 text-sm text-gray-600 hover:bg-emerald-50 hover:text-emerald-700 transition-colors border-b border-gray-50 last:border-0 flex items-center gap-3"
                    >
                      <Icon size={13} className="text-emerald-400 flex-shrink-0" />
                      <span>{i + 1}. {title}</span>
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* ── Main Content ── */}
          <main className="flex-1 min-w-0">

            {/* Intro card */}
            <div className="relative bg-gradient-to-br from-green-500 via-emerald-500 to-teal-600 rounded-2xl p-6 mb-10 text-white overflow-hidden shadow-lg shadow-emerald-200">
              <div className="absolute -top-8 -right-8 w-40 h-40 bg-white/10 rounded-full blur-2xl pointer-events-none" />
              <div className="absolute -bottom-8 -left-8 w-40 h-40 bg-white/10 rounded-full blur-2xl pointer-events-none" />
              <div className="relative flex items-center gap-2 mb-3">
                <Sparkles size={15} className="text-yellow-300" />
                <span className="text-white/80 text-xs font-bold uppercase tracking-widest">OneNest Connect Software Pvt. Ltd.</span>
                <Sparkles size={15} className="text-yellow-300" />
              </div>
              <p className="relative text-white/90 leading-relaxed text-sm">
                Your privacy is important to us. This policy explains how we collect, use, store, and
                protect your information when you visit our website, use our software, or engage with
                our team. By using our services, you agree to the practices described here.
              </p>
              <p className="relative text-white/50 text-xs mt-4">Last Updated: 26 May 2026</p>
            </div>

            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-8 sm:p-10">

              {/* 1 */}
              <Section id="about" icon={Sparkles} num={1} title="About Our Company">
                <p className="text-gray-600 text-sm leading-relaxed mb-3">
                  OneNest Connect Software Pvt. Ltd. is an IT solutions and digital services company
                  specializing in end-to-end technology solutions.
                </p>
                <BulletList items={[
                  "Website & Mobile Application Development",
                  "CRM, HRMS & ERP Solutions",
                  "Digital Marketing & SEO Services",
                  "UI/UX Design & Software Consulting",
                  "Cloud & Hosting Solutions",
                  "E-Commerce Development",
                  "Training & Internship Programs",
                ]} />
                <p className="text-gray-500 text-sm mt-4">
                  We serve startups, businesses, educational institutions, NGOs, and enterprises across
                  multiple industries.
                </p>
              </Section>

              <div className="border-t border-gray-100 my-8" />

              {/* 2 */}
              <Section id="collect" icon={Eye} num={2} title="Information We Collect">
                <SubSection title="A. Personal Information">
                  <p className="text-gray-500 text-xs mb-2">Collected when you contact us, register, or submit forms:</p>
                  <BulletList items={[
                    "Full Name, Mobile Number, Email Address",
                    "Company / Organization Name",
                    "Billing Address & GST Information",
                    "Project Requirements & Social Media Links",
                    "Identity Information (for verification when required)",
                  ]} />
                </SubSection>
                <SubSection title="B. Technical & Usage Information">
                  <p className="text-gray-500 text-xs mb-2">Automatically collected when you visit our website:</p>
                  <BulletList items={[
                    "IP Address, Browser Type & Version",
                    "Operating System & Device Type",
                    "Referral Source, Pages Visited, Session Duration",
                    "Date & Time of Visit, Website Interaction Data",
                  ]} />
                </SubSection>
                <SubSection title="C. Payment Information">
                  <BulletList items={[
                    "Transaction Details & Invoice Information",
                    "Payment References & Billing Information",
                  ]} />
                  <div className="mt-3">
                    <InfoChip
                      icon={Lock}
                      color="green"
                      text="Sensitive payment data (card numbers) is processed by trusted third-party gateways and is never stored on our servers."
                    />
                  </div>
                </SubSection>
              </Section>

              <div className="border-t border-gray-100 my-8" />

              {/* 3 */}
              <Section id="use" icon={Cpu} num={3} title="How We Use Your Information">
                {[
                  { title: "Service Delivery", items: ["Developing websites and applications", "Managing CRM/ERP systems", "Delivering software solutions", "Providing hosting and maintenance support"] },
                  { title: "Customer Support", items: ["Responding to inquiries", "Providing technical assistance", "Resolving issues and complaints"] },
                  { title: "Business Operations", items: ["Invoice generation & payment processing", "Project communication & client management"] },
                  { title: "Marketing & Promotions", items: ["Service updates, newsletters, and promotions", "Users may opt out of marketing communications at any time"] },
                  { title: "Security & Compliance", items: ["Fraud prevention & legal compliance", "Data protection & system monitoring"] },
                ].map((s) => (
                  <SubSection key={s.title} title={s.title}>
                    <BulletList items={s.items} />
                  </SubSection>
                ))}
              </Section>

              <div className="border-t border-gray-100 my-8" />

              {/* 4 */}
              <Section id="cookies" icon={Package} num={4} title="Cookies & Tracking Technologies">
                <p className="text-gray-600 text-sm leading-relaxed mb-3">
                  Our website uses cookies, tracking pixels, and analytics tools to enhance your experience. Cookies help us:
                </p>
                <BulletList items={[
                  "Remember user preferences",
                  "Improve website speed and functionality",
                  "Analyze traffic and visitor behavior",
                  "Optimize marketing campaigns",
                ]} />
                <p className="text-gray-400 text-xs mt-4">
                  You may disable cookies in browser settings; some features may not function properly if disabled.
                </p>
              </Section>

              <div className="border-t border-gray-100 my-8" />

              {/* 5 */}
              <Section id="third" icon={Link2} num={5} title="Third-Party Services">
                <p className="text-gray-600 text-sm leading-relaxed mb-3">We use trusted third-party services, including:</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 mt-2">
                  {["Payment Gateway Providers", "Cloud Hosting Providers", "Domain & Email Services", "CRM Platforms", "Analytics Services", "Marketing Tools"].map((s) => (
                    <div key={s} className="flex items-center gap-2 bg-gray-50 border border-gray-100 rounded-lg px-3 py-2.5 text-xs text-gray-600">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
                      {s}
                    </div>
                  ))}
                </div>
                <p className="text-gray-400 text-xs mt-4">
                  These third parties process information according to their own privacy policies.
                </p>
              </Section>

              <div className="border-t border-gray-100 my-8" />

              {/* 6 */}
              <Section id="security" icon={Lock} num={6} title="Data Security">
                <p className="text-gray-600 text-sm mb-4">We implement strong security measures to protect your information:</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                  {[
                    { icon: "🔐", label: "SSL Encryption" },
                    { icon: "🏗️", label: "Secure Hosting" },
                    { icon: "🛡️", label: "Firewall Protection" },
                    { icon: "👁️", label: "Restricted Access" },
                    { icon: "🔑", label: "Secure Authentication" },
                    { icon: "📊", label: "Security Monitoring" },
                  ].map((item) => (
                    <div key={item.label} className="flex items-center gap-2.5 bg-emerald-50 border border-emerald-100 rounded-xl px-3 py-3 text-xs text-emerald-800 font-medium">
                      <span className="text-base leading-none">{item.icon}</span>
                      {item.label}
                    </div>
                  ))}
                </div>
                <p className="text-gray-400 text-xs mt-4">
                  While we take reasonable precautions, no online platform can guarantee complete security.
                </p>
              </Section>

              <div className="border-t border-gray-100 my-8" />

              {/* 7 */}
              <Section id="sharing" icon={Users} num={7} title="Data Sharing & Disclosure">
                <div className="mb-4">
                  <InfoChip icon={Shield} color="teal" text="We do not sell or rent your personal information — ever." />
                </div>
                <p className="text-gray-600 text-sm mb-2">We may disclose information only in these situations:</p>
                <BulletList items={[
                  "Legal obligations or government requests",
                  "Protection against fraud or cyber threats",
                  "Business operations with trusted partners",
                  "Payment processing and invoice verification",
                ]} />
                <p className="text-gray-400 text-xs mt-4">All third-party access is limited to necessary business functions.</p>
              </Section>

              <div className="border-t border-gray-100 my-8" />

              {/* 8 */}
              <Section id="retention" icon={RefreshCw} num={8} title="Data Retention Policy">
                <p className="text-gray-600 text-sm leading-relaxed mb-3">We retain information for as long as necessary to:</p>
                <BulletList items={[
                  "Deliver services and maintain legal compliance",
                  "Resolve disputes & enforce agreements",
                  "Improve customer support",
                ]} />
                <p className="text-gray-500 text-sm mt-4">Once data is no longer required, it is securely deleted or anonymized.</p>
              </Section>

              <div className="border-t border-gray-100 my-8" />

              {/* 9 */}
              <Section id="rights" icon={Shield} num={9} title="Your Privacy Rights">
                <p className="text-gray-600 text-sm mb-4">Depending on applicable laws, you may have the right to:</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  {[
                    { icon: Eye,     right: "Access your personal information" },
                    { icon: Edit3,   right: "Correct inaccurate data" },
                    { icon: Trash2,  right: "Request deletion of data" },
                    { icon: Mail,    right: "Withdraw marketing consent" },
                    { icon: Package, right: "Request data portability" },
                    { icon: AlertTriangle, right: "Restrict certain processing activities" },
                  ].map(({ icon: Icon, right }) => (
                    <div key={right} className="flex items-center gap-3 bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-100 rounded-xl px-4 py-3 text-sm text-gray-700">
                      <Icon size={14} className="text-emerald-500 flex-shrink-0" />
                      {right}
                    </div>
                  ))}
                </div>
                <p className="text-gray-500 text-xs mt-4">
                  To exercise these rights, contact us at{" "}
                  <a href="mailto:info@onenestconnect.in" className="text-emerald-600 hover:underline font-medium">
                    info@onenestconnect.in
                  </a>
                </p>
              </Section>

              <div className="border-t border-gray-100 my-8" />

              {/* 10 */}
              <Section id="children" icon={Users} num={10} title="Children's Privacy">
                <p className="text-gray-600 text-sm leading-relaxed">
                  Our services are intended for individuals{" "}
                  <span className="font-semibold text-gray-800">above 18 years of age</span>. We do not
                  knowingly collect personal information from children. If such information is identified,
                  it will be removed promptly.
                </p>
              </Section>

              <div className="border-t border-gray-100 my-8" />

              {/* 11 */}
              <Section id="transfers" icon={Globe} num={11} title="International Data Transfers">
                <p className="text-gray-600 text-sm leading-relaxed">
                  If you access our services outside India, your information may be transferred and
                  processed in India or other countries where our service providers operate. By using
                  our services, you consent to such transfers.
                </p>
              </Section>

              <div className="border-t border-gray-100 my-8" />

              {/* 12 */}
              <Section id="ip" icon={Edit3} num={12} title="Intellectual Property & Content Protection">
                <p className="text-gray-600 text-sm leading-relaxed">
                  All website content, graphics, branding, software, source code, logos, and materials
                  developed by OneNest Connect Software Pvt. Ltd. are protected under applicable
                  intellectual property laws. Unauthorized copying, redistribution, or misuse is
                  strictly prohibited.
                </p>
              </Section>

              <div className="border-t border-gray-100 my-8" />

              {/* 13 */}
              <Section id="links" icon={Link2} num={13} title="External Links">
                <p className="text-gray-600 text-sm leading-relaxed">
                  Our website may contain links to third-party websites or applications. We are not
                  responsible for the privacy practices, security, or content of external platforms.
                  Users are encouraged to review third-party privacy policies separately.
                </p>
              </Section>

              <div className="border-t border-gray-100 my-8" />

              {/* 14 */}
              <Section id="changes" icon={RefreshCw} num={14} title="Changes to This Privacy Policy">
                <p className="text-gray-600 text-sm leading-relaxed">
                  We reserve the right to update or modify this Privacy Policy at any time without prior
                  notice. Changes become effective immediately upon posting on this page. Users are
                  encouraged to review this page periodically.
                </p>
              </Section>

              <div className="border-t border-gray-100 my-8" />

              {/* 15 */}
              <Section id="contact" icon={Mail} num={15} title="Contact Information">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {[
                    { icon: Mail,    label: "Email",    value: "info@onenestconnect.in", href: "mailto:info@onenestconnect.in" },
                    { icon: Phone,   label: "Phone",    value: "+91 8588942008",           href: "tel:+918588942008" },
                    { icon: Globe,   label: "Website",  value: "www.onenestconnect.in",    href: "https://www.onenestconnect.in" },
                    { icon: MapPin,  label: "Location", value: "Sector 44, Noida, UP – 201301", href: null },
                  ].map(({ icon: Icon, label, value, href }) => (
                    <div key={label} className="flex items-start gap-3 bg-gray-50 border border-gray-100 rounded-xl p-4">
                      <div className="w-9 h-9 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm shadow-emerald-200">
                        <Icon size={14} className="text-white" />
                      </div>
                      <div>
                        <p className="text-[10px] text-gray-400 font-semibold uppercase tracking-widest">{label}</p>
                        {href ? (
                          <a href={href} className="text-sm text-emerald-600 hover:underline mt-0.5 block font-medium">{value}</a>
                        ) : (
                          <p className="text-sm text-gray-600 mt-0.5">{value}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </Section>

              {/* ── User Consent CTA ── */}
              <div className="mt-10 pt-8 border-t border-gray-100">
                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="relative bg-gradient-to-br from-green-500 via-emerald-500 to-teal-600 rounded-2xl p-8 text-center overflow-hidden"
                >
                  <div className="absolute -top-10 -right-10 w-48 h-48 bg-white/10 rounded-full blur-3xl pointer-events-none" />
                  <div className="absolute -bottom-10 -left-10 w-48 h-48 bg-white/10 rounded-full blur-3xl pointer-events-none" />
                  <div className="relative flex justify-center mb-4">
                    <Sparkles size={15} className="text-yellow-300 mr-1.5" />
                    <span className="text-white/80 text-xs font-bold uppercase tracking-widest">User Consent</span>
                    <Sparkles size={15} className="text-yellow-300 ml-1.5" />
                  </div>
                  <p className="relative text-white/90 text-sm leading-relaxed max-w-lg mx-auto">
                    By accessing our website, submitting information, or using our services, you
                    acknowledge that you have read, understood, and agreed to this Privacy Policy and
                    our terms of service.
                  </p>
                  <div className="relative mt-6 flex flex-wrap justify-center gap-3">
                    <Link
                      to="/"
                      className="bg-white text-emerald-700 text-sm font-bold px-6 py-2.5 rounded-full hover:shadow-lg transition-all flex items-center gap-2"
                    >
                      Back to Home <ArrowRight size={14} />
                    </Link>
                    <Link
                      to="/contact"
                      className="bg-emerald-600/60 border border-emerald-400/50 text-white text-sm font-semibold px-6 py-2.5 rounded-full hover:bg-emerald-600/80 transition-all"
                    >
                      Contact Us
                    </Link>
                  </div>
                </motion.div>
              </div>

            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;